import React from 'react';
import {
    Col, Row,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { isEqual } from 'lodash-es';
import {
    faCheckCircle, faExclamationTriangle, faWarehouse,
} from '@fortawesome/free-solid-svg-icons';
import { FormPrompt } from '@s20/components/common/FormPrompt';
import { clone } from '@s20/common/util/object';
import { httpClient } from '@s20/services/httpClient';
import {
    AppPageForm, FlexCenterRow, FormDivider, onFieldChange, onReactSelectChanged, ToastMessage,
    Input,
} from '../common/forms/FormElements';
import { BaseFormViewModel } from '../common/ViewModel';
import CommonContext, { ApiRoutes, AppNavPaths, ServiceResponseResult } from '../Common';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { handleFormSaveError } from '../common/forms/ValidationError';
import { SaveButton } from '../common/forms/SaveButton';
import { Role } from './Role';

class RoleForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();

        const stateBase = {
            role: new Role(),
            permissions: [],
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;
        this.onSelectChanged = this.onSelectChanged.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onChange = this.onChange.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    componentDidUpdate(prevProps) {
        if (prevProps && (this.props.match.params.id !== (prevProps.match.params ?? {}).id)) {
            this.populateState();
        }
    }

    onChange = onFieldChange;

    onClearErrors = () => this.setState({ errors: {} });

    onClose = (response) => {
        this.resetForm();
        this.context.setFormOpened(false);
        this.props.onClose(response);
    };

    onDelete = async () => {
        const { role } = this.state;
        const response = await httpClient.delete(ApiRoutes.role.delete(role.id)).catch(this.onCatchFetchError);
        if (response) this.onClose(response);
    };

    onRoleSaved = (response, redirect) => {
        if (response.result === ServiceResponseResult.Ok) {
            if (redirect) {
                toast.success(
                    <ToastMessage
                        icon={faCheckCircle}
                        header="Save Successful"
                        message="Saved."
                    />,
                );
                this.context.history.push(`${AppNavPaths.Role}/${response.data}`);
            } else {
                toast.success(
                    <ToastMessage
                        icon={faCheckCircle}
                        header="Save Successful"
                        message={`[${response.data.displayName}] saved.`}
                    />,
                );
                this.context.history.replace(this.props.location.pathname);
            }
        } else {
            toast.error(
                <ToastMessage
                    icon={faExclamationTriangle}
                    header="There was a problem saving"
                    message={`${response.message}`}
                />);
        }
    };

    onSelectChanged = onReactSelectChanged;

    onSubmit = async () => {
        // Clear any fluent api errors
        this.onClearErrors();
        this.setSaving(true);

        const { role } = { ...this.state };

        if (this.props.location.pathname == AppNavPaths.RoleNew) {
            await this.createRole(role);
        } else {
            await this.updateRole(role);
        }
    };

    setSaving = (b) => this.setState({ saving: b });

    createRole = async (role) => {
        const redirect = true;
        const response = await httpClient.post(ApiRoutes.role.create(), role)
            .catch(this.handleSaveError);

        if (response) {
            this.onRoleSaved(response, redirect);
        }

        this.setSaving(false);
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    async populateState() {
        const roleId = this.props.match.params.id;

        const [permissions, role] = await Promise.all([
            httpClient.js(ApiRoutes.typeAheads.permissions()),
            roleId ? httpClient.js(ApiRoutes.role.byId(roleId)) : new Role(),
        ]);

        const originalData = clone(role);

        this.setState({
            originalData,
            role,
            loading: false,
            permissions,
            saving: false,
        });
    }

    resetForm = () => this.setState({ formValidated: false });

    updateRole = async (role) => {
        const redirect = false;
        const response = await httpClient.put(ApiRoutes.role.byId(role.id), role)
            .catch(this.handleSaveError);

        if (response) {
            this.onRoleSaved(response, redirect);
        }
        this.setSaving(false);
    };

    render() {
        const {
            role,
            originalData,
            errors,
            formValidated,
            permissions,
            saving,
        } = this.state;
        const existing = !!role.id;
        return (
            <>
                <FormPrompt
                    when={!saving && !isEqual(originalData, role)}
                    message="You have unsaved changes, are you sure you want to leave?"
                />
                <AppPageForm
                    backUrl={AppNavPaths.Roles}
                    backLabel="Back to Roles"
                    formShown={this.context.formIsOpen}
                    formId="roleForm"
                    formHeadingIcon={faWarehouse}
                    formHeading={!role.id ? 'New Role' : 'Edit Role'}
                    formName="roleForm"
                    formRef={this.formRef}
                    onSubmit={this.onSubmit}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={formValidated}
                    saving={saving}
                    errors={errors}
                    onClearErrors={this.onClearErrors}
                    loading={this.state.loading}
                >
                    <Row className="pt-3">
                        <Col
                            xl="6"
                            lg="8"
                            md="10"
                            sm="12"
                            className="ml-auto mr-auto"
                        >
                            <Input
                                label="Name"
                                id="roleName"
                                name="role.roleName"
                                value={role.roleName ?? ''}
                                onChange={this.onChange}
                                placeholder="Enter Name (max 150 characters)"
                                maxLength="150"
                                pattern="[^()/><\][\\\x22,;|]+"
                                error="Name is required and can only contain the following special characters: hyphens and periods."
                                required
                            />

                            <ValidatedSelect
                                id="types"
                                name="role.permissions"
                                label="Permissions"
                                required
                                isMulti
                                options={permissions}
                                value={(permissions ?? []).filter((x) => (role.permissions ?? []).includes(x.value)) ?? ''}
                                onChange={this.onSelectChanged}
                                error="A user permission is required."
                            />
                        </Col>
                    </Row>
                    <FormDivider />

                    <FlexCenterRow className="mb-3">
                        <SaveButton
                            saving={!!this.state.saving}
                            label={!existing ? 'Save New Role' : 'Save'}
                        />
                    </FlexCenterRow>

                </AppPageForm>
            </>
        );
    }
}
export default RoleForm;
